/**
 * @description Dependency Checkbox implementation. A Dependency Checkbox has Parents and/or Children, and checking/unchecking controls them accordingly.
 * @param InputCheckbox Base widget for extending
 * @returns Input Checkbox class
 */
function InputDependencyCheckboxClassCreator(InputCheckbox: ReturnType<typeof import('widgets/forms/InputCheckbox').default>) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputDependencyCheckbox
     * @augments InputCheckbox
     *
     * @classdesc Dependency Checkbox implementation. Extends a standard checkbox by dependency functionality.
     *
     * Each dependency Checkboxes can have a parent and/or multiple children.
     *
     * In order to trigger correctly, the checkbox requires two events:
     *  - data-widget-event-change handleDependentCheckboxes (Widget Event)
     *  - data-event-change handleChange (DOM Event, required to trigger the Widget Event)
     *  - => Both are covered by the field types (see below)
     *
     * Requires a {@link FormDependencyCheckboxesMixin} as the containing form in order to work.
     *
     * There are three predefined field types:
     *  - fields.generic.dependencyCheckboxParent (Parent only)
     *  - fields.generic.dependencyCheckboxChild (Child only)
     *  - fields.generic.dependencyCheckboxParentChild (Child + Parent)
     *
     * @property {string} data-widget - Widget name `inputCheckbox`
     *
     * @property {string} data-is-dependent Set true if the checkbox is a dependent (covered by field type)
     * @property {string} data-parent-id The parent ID if the dependent checkbox
     *
     * @property {string} data-is-parent Set true if the checkbox is a parent (covered by field type)
     * @property {Array} data-children List of children for a parent checkbox
     *
     * @example <caption>InputCheckbox definition in dynamicForms.js</caption>
     * ...
     *      acceptAllToggle: {
     *          extends: 'fields.generic.dependencyCheckboxParent',
     *          widget: {
     *              attributes: {
     *                  'data-id': 'acceptAllToggle',
     *                  'data-children': [
     *                      'privacyPersonalInformation',
     *                      'privacyPersonalInformationTransfer',
     *                      'promotionalInformationToggle'
     *                  ]
     *              }
     *          },
     *          'label.text': 'form.profile.acceptalltoggle.label'
     *      },
     */
    class InputDependencyCheckbox extends InputCheckbox {
        /**
         * Default prefs
         *
         * @returns Preferences
         */
        prefs() {
            return {
                isParent: false,
                isDependent: false,
                children: '',
                parentId: '',
                ...super.prefs()
            };
        }

        /**
         * Checks if the checkbox was configured as a parent
         * A checkbox cannot be a parent if it doesn't have any children.
         *
         * @returns True if this checkbox is a parent
         */
        isParent() {
            return this.prefs().isParent;
        }

        /**
         * Returns a list of child checkbox IDs
         *
         * @returns Array of child IDs
         */
        getChildren() {
            if (!this.isParent()) {
                return [];
            }

            const childrenString = this.prefs().children;
            const childIDS = childrenString.split(',');

            return childIDS;
        }

        /**
         * Checks if the checkbox is configured as a Dependent
         * A checkbox cannot be a dependent if it doesn't have a parent
         *
         * @returns True if the checkbox is a dependent
         */
        isDependent() {
            return this.prefs().isDependent && this.getParent();
        }

        /**
         * Returns the parent checkbox ID
         *
         * @returns A string containing the parent form field ID
         */
        getParent() {
            return this.prefs().parentId;
        }
    }

    return InputDependencyCheckbox;
}

export type TInputDependencyCheckbox = ReturnType<typeof InputDependencyCheckboxClassCreator>;

export type TInputDependencyCheckboxInstance = InstanceType<TInputDependencyCheckbox>;

export default InputDependencyCheckboxClassCreator;
